import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Globe, ImageIcon, Loader2, Save, Settings, Share2, Store } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import { ApiError } from '../../lib/api';
import { getSiteSettings, updateSiteSettings } from '../../services/siteSettings';
import type {
  SiteSettings,
  SiteSettingsFiles,
  SiteSettingsImageKey,
  SiteSettingsTextKey,
} from '../../types/siteSettings';

type FormValues = Record<SiteSettingsTextKey, string>;

const TEXT_KEYS: SiteSettingsTextKey[] = [
  'site_name',
  'site_tagline',
  'contact_email',
  'contact_phone',
  'address',
  'facebook_url',
  'instagram_url',
  'twitter_url',
];

const IMAGE_KEYS: SiteSettingsImageKey[] = ['logo', 'favicon', 'og_image'];

const IMAGE_LIMITS: Record<SiteSettingsImageKey, number> = {
  logo: 2 * 1024 * 1024,
  favicon: 1 * 1024 * 1024,
  og_image: 4 * 1024 * 1024,
};

const IMAGE_LABELS: Record<SiteSettingsImageKey, string> = {
  logo: 'Logo',
  favicon: 'Favicon',
  og_image: 'Open Graph image',
};

function settingsToFormValues(data: SiteSettings): FormValues {
  return TEXT_KEYS.reduce((acc, key) => {
    acc[key] = data[key] ?? '';
    return acc;
  }, {} as FormValues);
}

function Field({
  label,
  id,
  value,
  onChange,
  type = 'text',
  multiline = false,
}: {
  label: string;
  id: SiteSettingsTextKey;
  value: string;
  onChange: (id: SiteSettingsTextKey, value: string) => void;
  type?: string;
  multiline?: boolean;
}) {
  const shared =
    'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100';

  return (
    <label htmlFor={id} className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      {multiline ? (
        <textarea
          id={id}
          rows={3}
          value={value}
          onChange={(e) => onChange(id, e.target.value)}
          className={`${shared} resize-y min-h-[88px]`}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(id, e.target.value)}
          className={shared}
        />
      )}
    </label>
  );
}

function ImageField({
  id,
  label,
  currentUrl,
  file,
  maxBytes,
  onFileChange,
}: {
  id: SiteSettingsImageKey;
  label: string;
  currentUrl: string | null;
  file: File | undefined;
  maxBytes: number;
  onFileChange: (id: SiteSettingsImageKey, file: File | undefined) => void;
}) {
  const preview = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return currentUrl;
  }, [file, currentUrl]);

  useEffect(() => {
    if (!file || !preview?.startsWith('blob:')) return;
    return () => URL.revokeObjectURL(preview);
  }, [file, preview]);

  const maxMb = (maxBytes / (1024 * 1024)).toFixed(0);

  return (
    <div className="block sm:col-span-2">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/50 p-4 sm:flex-row sm:items-start">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
          {preview ? (
            <img src={preview} alt="" className="h-full w-full object-contain" />
          ) : (
            <ImageIcon className="h-8 w-8 text-slate-300" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <input
            id={id}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const next = e.target.files?.[0];
              if (!next) {
                onFileChange(id, undefined);
                return;
              }
              if (next.size > maxBytes) {
                toast.error(`${label} must be ${maxMb} MB or smaller`);
                e.target.value = '';
                return;
              }
              onFileChange(id, next);
            }}
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100"
          />
          <p className="mt-1.5 text-xs text-slate-500">Image, max {maxMb} MB</p>
          {file && (
            <p className="mt-1 truncate text-xs text-indigo-600">Selected: {file.name}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  icon: Icon,
  children,
}: {
  title: string;
  description: string;
  icon: typeof Store;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
      <div className="mb-5 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 shadow-sm ring-4 ring-indigo-100">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="mt-0.5 text-sm text-slate-500">{description}</p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

const SiteSettingsPage = () => {
  const [form, setForm] = useState<FormValues>(() =>
    TEXT_KEYS.reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {} as FormValues),
  );
  const [images, setImages] = useState<Pick<SiteSettings, SiteSettingsImageKey>>({
    logo: null,
    favicon: null,
    og_image: null,
  });
  const [pendingFiles, setPendingFiles] = useState<SiteSettingsFiles>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await getSiteSettings();
        if (cancelled) return;
        setForm(settingsToFormValues(data));
        setImages({
          logo: data.logo,
          favicon: data.favicon,
          og_image: data.og_image,
        });
      } catch {
        if (!cancelled) toast.error('Failed to load site settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (id: SiteSettingsTextKey, value: string) => {
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleFileChange = (id: SiteSettingsImageKey, file: File | undefined) => {
    setPendingFiles((prev) => {
      const next = { ...prev };
      if (file) next[id] = file;
      else delete next[id];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updated = await updateSiteSettings(form, pendingFiles);
      setForm(settingsToFormValues(updated));
      setImages({
        logo: updated.logo,
        favicon: updated.favicon,
        og_image: updated.og_image,
      });
      setPendingFiles({});
      toast.success('Site settings saved');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Failed to save site settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col w-full">
      <div className="flex flex-1 w-full">
        <Sidebar />
        <main className="flex-1 p-4 md:p-8 w-full max-h-screen overflow-y-auto">
          <div className="mb-6 md:mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-xs font-medium text-indigo-700 mb-3">
              <Settings className="h-3.5 w-3.5" />
              Store configuration
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Site Settings
            </h1>
            <p className="mt-1.5 text-sm md:text-base text-slate-500">
              Manage your storefront identity, branding assets, and contact details.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <Section
                title="General"
                description="Basic store information shown across your website."
                icon={Store}
              >
                <Field
                  label="Site name"
                  id="site_name"
                  value={form.site_name}
                  onChange={handleChange}
                />
                <Field
                  label="Site tagline"
                  id="site_tagline"
                  value={form.site_tagline}
                  onChange={handleChange}
                />
              </Section>

              <Section
                title="Branding"
                description="Logo, favicon, and social preview image."
                icon={ImageIcon}
              >
                {IMAGE_KEYS.map((key) => (
                  <ImageField
                    key={key}
                    id={key}
                    label={IMAGE_LABELS[key]}
                    currentUrl={images[key]}
                    file={pendingFiles[key]}
                    maxBytes={IMAGE_LIMITS[key]}
                    onFileChange={handleFileChange}
                  />
                ))}
              </Section>

              <Section
                title="Contact"
                description="How customers can reach your business."
                icon={Globe}
              >
                <Field
                  label="Contact email"
                  id="contact_email"
                  type="email"
                  value={form.contact_email}
                  onChange={handleChange}
                />
                <Field
                  label="Contact phone"
                  id="contact_phone"
                  value={form.contact_phone}
                  onChange={handleChange}
                />
                <div className="sm:col-span-2">
                  <Field
                    label="Address"
                    id="address"
                    value={form.address}
                    onChange={handleChange}
                    multiline
                  />
                </div>
              </Section>

              <Section
                title="Social links"
                description="Profiles linked from your storefront."
                icon={Share2}
              >
                <Field
                  label="Facebook URL"
                  id="facebook_url"
                  type="url"
                  value={form.facebook_url}
                  onChange={handleChange}
                />
                <Field
                  label="Instagram URL"
                  id="instagram_url"
                  type="url"
                  value={form.instagram_url}
                  onChange={handleChange}
                />
                <div className="sm:col-span-2">
                  <Field
                    label="Twitter / X URL"
                    id="twitter_url"
                    type="url"
                    value={form.twitter_url}
                    onChange={handleChange}
                  />
                </div>
              </Section>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-200/50 transition hover:from-indigo-700 hover:to-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saving ? 'Saving…' : 'Save settings'}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default SiteSettingsPage;
